/**
 * EvaluationProgress Component
 *
 * Displays real-time progress for multi-study batch evaluation.
 * Shows overall progress and individual protocol status with appropriate
 * icons for pending, processing, completed, and failed states.
 */

import { CheckCircle, Circle, XCircle, Loader2, AlertCircle } from 'lucide-react';

export interface ProtocolProgress {
  protocolId: string;
  protocolName: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number; // 0-100
  error?: string;
}

export interface EvaluationProgressProps {
  evaluationId: string;
  protocols: ProtocolProgress[];
  overallProgress: number; // 0-100
}

/**
 * Status icon component with appropriate styling for each state
 */
function StatusIcon({ status }: { status: ProtocolProgress['status'] }) {
  switch (status) {
    case 'completed':
      return <CheckCircle size={18} className="text-green-500 dark:text-green-400 flex-shrink-0" />;
    case 'processing':
      return <Loader2 size={18} className="text-primary-600 dark:text-primary-400 animate-spin flex-shrink-0" />;
    case 'failed':
      return <XCircle size={18} className="text-red-500 dark:text-red-400 flex-shrink-0" />;
    case 'pending':
    default:
      return <Circle size={18} className="text-gray-300 dark:text-gray-600 flex-shrink-0" />;
  }
}

/**
 * Individual protocol progress row
 */
function ProtocolProgressRow({ protocol }: { protocol: ProtocolProgress }) {
  const { protocolName, status, progress, error } = protocol;

  return (
    <div className="py-3 border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <div className="flex items-center gap-3">
        <StatusIcon status={status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span
              className={`text-sm font-medium truncate ${
                status === 'failed' ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'
              }`}
            >
              {protocolName}
            </span>
            {status === 'processing' && progress !== undefined && (
              <span className="text-xs text-primary-600 dark:text-primary-400 font-medium ml-2">{progress}%</span>
            )}
            {status === 'completed' && (
              <span className="text-xs text-green-600 dark:text-green-400 font-medium ml-2">Complete</span>
            )}
          </div>

          {/* Progress bar for processing state */}
          {status === 'processing' && progress !== undefined && (
            <div className="mt-2 w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}

          {/* Error message for failed state */}
          {status === 'failed' && error && (
            <div className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400">
              <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function EvaluationProgress({
  evaluationId,
  protocols,
  overallProgress,
}: EvaluationProgressProps) {
  // Calculate summary statistics
  const completedCount = protocols.filter((p) => p.status === 'completed').length;
  const failedCount = protocols.filter((p) => p.status === 'failed').length;
  const totalCount = protocols.length;

  // Determine overall status
  const isComplete = completedCount + failedCount === totalCount;
  const hasFailures = failedCount > 0;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header with overall progress */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Multi-Study Evaluation Progress</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedCount} / {totalCount} protocols
          </span>
        </div>

        {/* Overall progress bar */}
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-500 ${
              isComplete
                ? hasFailures
                  ? 'bg-amber-500'
                  : 'bg-green-500'
                : 'bg-primary-600'
            }`}
            style={{ width: `${overallProgress}%` }}
          />
        </div>

        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {isComplete
              ? hasFailures
                ? `Completed with ${failedCount} error${failedCount > 1 ? 's' : ''}`
                : 'All protocols evaluated'
              : 'Evaluating...'}
          </span>
          <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{overallProgress}%</span>
        </div>
      </div>

      {/* Protocol list */}
      <div className="px-4 py-2 max-h-80 overflow-y-auto">
        {protocols.map((protocol) => (
          <ProtocolProgressRow key={protocol.protocolId} protocol={protocol} />
        ))}
      </div>

      {/* Completion message */}
      {isComplete && (
        <div
          className={`px-4 py-3 border-t ${
            hasFailures
              ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
              : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
          }`}
        >
          <div
            className={`flex items-center gap-2 ${
              hasFailures ? 'text-amber-700 dark:text-amber-300' : 'text-green-700 dark:text-green-300'
            }`}
          >
            {hasFailures ? (
              <>
                <AlertCircle size={16} />
                <span className="text-sm font-medium">
                  Evaluation completed with {failedCount} failed protocol
                  {failedCount > 1 ? 's' : ''}
                </span>
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                <span className="text-sm font-medium">
                  All {totalCount} protocols evaluated successfully
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default EvaluationProgress;
